/* eslint-disable @typescript-eslint/no-unsafe-call */
import {
  OrderStatus,
  getAddressLabels,
  getOrderListFn,
  type Path,
} from '@ahm/api-wrappers-core';
import { decode } from '@mapbox/polyline';
import {
  default as WebMercatorViewport,
  type FitBoundsOptions,
} from '@math.gl/web-mercator';
import { lineString } from '@turf/helpers';
import dayjs from 'dayjs';
import type { PathWithIndexes } from '../types';

/**
 * Decodes a polyline string and converts it to a GeoJSON LineString.
 * @param {string} polyString - The encoded polyline string.
 * @returns A GeoJSON LineString object.
 */
export type Position = number[];
export type GeoJSONLineString = GeoJSON.Feature<GeoJSON.LineString>;
export const decodePolylineToGeoJSON = (
  polyString: string
): GeoJSONLineString => {
  const decodedPolyline = decode(polyString);
  return lineString(decodedPolyline.map((point) => [point[1], point[0]]));
};

interface WebMercatorViewportProps {
  width: number;
  height: number;
  latitude?: number;
  longitude?: number;
  position?: number[];
  zoom?: number;
  pitch?: number;
  bearing?: number;
  altitude?: number;
  fovy?: number;
  nearZMultiplier?: number;
  farZMultiplier?: number;
}

export function getMercatorViewport(
  coordinates: number[][],
  webMercatorViewportConfig: WebMercatorViewportProps,
  fitBoundsOptions?: Omit<FitBoundsOptions, 'width' | 'height' | 'bounds'>
): WebMercatorViewport | null {
  try {
    const bounds: [[number, number], [number, number]] = [
      [Infinity, Infinity],
      [-Infinity, -Infinity],
    ];

    coordinates.forEach(([lat, lng]) => {
      bounds[0][0] = Math.min(bounds[0][0], lng);
      bounds[0][1] = Math.min(bounds[0][1], lat);
      bounds[1][0] = Math.max(bounds[1][0], lng);
      bounds[1][1] = Math.max(bounds[1][1], lat);
    });

    // Use WebMercatorViewport to get center longitude/latitude and zoom
    return new WebMercatorViewport(webMercatorViewportConfig).fitBounds(
      bounds,
      fitBoundsOptions
    ); // Can also use option: offset: [0, -100]
  } catch (error) {
    return null;
  }
}

export function mergePointByLatLng(
  objects: Path[],
  checkFields: string[]
): PathWithIndexes[] {
  try {
    const mergedObjects = new Map<string, PathWithIndexes>();

    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      const key = `${obj.lat},${obj.lng}`; // Use lat and lng to create a unique key
      const existingObject = mergedObjects.get(key);

      if (existingObject) {
        const fieldsChanged = checkFields.some(
          (field) =>
            existingObject[field as keyof Path] !== obj[field as keyof Path]
        );

        if (fieldsChanged) {
          checkFields.forEach((field) => {
            existingObject[field as keyof Path] =
              `${String(existingObject[field as keyof Path])},${String(
                obj[field as keyof Path]
              )}` as never;
          });
        }

        existingObject.indexes.push(i);
      } else {
        const newObj = { ...obj, indexes: [i] } as PathWithIndexes;
        mergedObjects.set(key, newObj);
      }
    }

    return Array.from(mergedObjects.values());
  } catch (error) {
    return objects.map(
      (obj, index) => ({ ...obj, indexes: [index] }) as PathWithIndexes
    );
  }
}

export function calculateFrameIndex(
  radius: number,
  maxRadius = 360,
  totalFrames = 64
) {
  // Assuming you have 64 frames in the spritesheet and a maximum radius of 50

  // Calculate the frame index based on the radius
  const normalizedRadius = Math.min(radius, maxRadius);
  return Math.floor((normalizedRadius / maxRadius) * totalFrames);
}

export function detectLatestPickupOrder(): Promise<Path | null> {
  return new Promise((resolve, reject) => {
    const ratingExpireTime = 72; // 72h
    const from_time = dayjs().subtract(ratingExpireTime, 'hour').unix();
    const to_time = dayjs().unix();

    const params = {
      count: 1,
      offset: 0,
      from_time,
      to_time,
      status: OrderStatus.Completed,
    };

    getOrderListFn(params)
      .then((orderResponse) => {
        if (orderResponse && orderResponse.length > 0) {
          const pickupOfOrder = orderResponse[0]?.path[0];

          if (!pickupOfOrder?.address) {
            resolve(null);
            return;
          }

          const newPickup = {
            ...pickupOfOrder,
            first_label: getAddressLabels(pickupOfOrder?.address)[0],
            second_label: getAddressLabels(pickupOfOrder.address)[1],
          };

          resolve(newPickup);
        } else {
          resolve(null);
        }
      })
      .catch((error) => {
        console.log('err', error);
        reject(error);
      });
  });
}

export type CoordinateArray = [number, number][] | number[][];
// Function to remove duplicates
export function flattenCoordinates(arr: CoordinateArray): CoordinateArray {
  const uniqueSet = new Set<string>();
  const result: number[][] = [];

  arr.forEach((item) => {
    const key = item.join(','); // Create a unique key for each subarray
    if (!uniqueSet.has(key)) {
      uniqueSet.add(key);
      result.push(item);
    }
  });

  return result;
}

export function setMapLogoElStyle(logoEl: HTMLLinkElement | HTMLAnchorElement) {
  logoEl.href = 'https://ahamove.com';
  logoEl.target = '_blank';
  logoEl.style.width = '116px';
  logoEl.style.height = '20px';
  logoEl.style.filter =
    'drop-shadow(0 1px 2px rgb(0 0 0 / 0.1)) drop-shadow(0 1px 1px rgb(0 0 0 / 0.06))';
  logoEl.style.backgroundImage = `url("data:image/svg+xml;charset=utf-8,%3Csvg%20width%3D%22116%22%20height%3D%2220%22%20viewBox%3D%220%200%20521%2090%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%20%3Cg%20clip-path%3D%22url(%23clip0_46938_115605)%22%3E%20%3Cpath%20d%3D%22M171.82%2013.6834L147.784%2077.9606H160.739L166.62%2061.8712H191.828L197.559%2077.9606H210.574L186.588%2013.6834H171.82ZM170.878%2049.8318L179.154%2027.0461L187.43%2049.8318H170.878Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M245.872%2035.4767C243.237%2034.0031%20240.442%2033.2613%20237.496%2033.2613C233.979%2033.2613%20230.813%2034.4241%20227.998%2036.7398V13.6834H215.955V77.9606H227.998V56.1272C227.998%2044.589%20233.859%2044.178%20235.712%2044.178C239.47%2044.178%20242.485%2047.1753%20242.485%2055.5859V77.9505H254.529V53.9719C254.529%2048.699%20253.647%2044.5288%20251.894%2041.4513C250.501%2038.9452%20248.497%2036.9503%20245.872%2035.4767Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M353.509%2033.2312C348.99%2033.2312%20344.952%2034.765%20341.375%2037.8425C338.049%2034.765%20334.442%2033.2312%20330.554%2033.2312C327.288%2033.2312%20324.252%2034.3941%20321.427%2036.7097V34.1435H309.484V77.9305H321.527V54.9142C321.527%2045.3108%20327.018%2044.7694%20328.26%2044.7694C330.304%2044.7694%20334.602%2046.4435%20334.602%2054.7839V77.9305H346.645V54.9142C346.645%2045.5213%20351.344%2044.6592%20353.338%2044.6592C356.144%2044.6592%20359.62%2047.466%20359.62%2054.7839V77.9305H371.663V54.1223C371.663%2048.8494%20370.822%2044.7795%20369.129%2041.8924C365.922%2036.1183%20360.722%2033.2312%20353.509%2033.2312Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M454.693%2034.1234L444.403%2060.0568L434.244%2034.1234H420.848L439.113%2077.9606H449.743L467.958%2034.1234H454.693Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M488.297%2033.2613C482.336%2033.2613%20477.286%2035.4266%20473.148%2039.7572C469.01%2044.0878%20466.936%2049.3907%20466.936%2055.6661C466.936%2062.0015%20469.02%2067.3045%20473.188%2071.575C477.356%2075.8454%20482.576%2077.9706%20488.848%2077.9706C497.184%2077.9706%20503.777%2074.0209%20508.606%2066.1116L498.076%2061.9715C495.822%2065.5502%20492.716%2067.3346%20488.768%2067.3346C483.999%2067.3346%20480.833%2064.8285%20479.26%2059.8062H509.177C509.237%2058.6133%20509.278%2057.5206%20509.278%2056.5081C509.278%2049.8017%20507.304%2044.2481%20503.346%2039.8474C499.399%2035.4667%20494.379%2033.2613%20488.297%2033.2613ZM479.26%2051.4257C480.703%2046.3433%20483.708%2043.8071%20488.297%2043.8071C492.686%2043.8071%20495.691%2046.3433%20497.325%2051.4257H479.26Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M399.537%2033.0708C387.173%2033.0708%20377.144%2043.1054%20377.144%2055.4756C377.144%2067.8458%20387.173%2077.8804%20399.537%2077.8804C411.901%2077.8804%20421.93%2067.8458%20421.93%2055.4756C421.93%2043.1054%20411.911%2033.0708%20399.537%2033.0708ZM399.557%2067.1541C393.235%2067.1541%20388.115%2062.0316%20388.115%2055.7062C388.115%2049.3807%20393.235%2044.2582%20399.557%2044.2582C405.879%2044.2582%20410.999%2049.3807%20410.999%2055.7062C410.999%2062.0216%20405.879%2067.1541%20399.557%2067.1541Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M292.111%2037.1207C288.664%2034.5545%20285.057%2033.2613%20281.29%2033.2613C275.328%2033.2613%20270.269%2035.4366%20266.091%2039.7973C261.923%2044.1579%20259.839%2049.4108%20259.839%2055.5658C259.839%2061.781%20261.923%2067.0639%20266.091%2071.4246C270.259%2075.7852%20275.328%2077.9605%20281.29%2077.9605C285.047%2077.9605%20288.654%2076.6774%20292.111%2074.1011V77.1987H304.154V34.2637H292.111V37.1207ZM282.142%2067.4348C276.23%2067.4348%20271.441%2062.1319%20271.441%2055.5859C271.441%2049.0399%20276.23%2043.7269%20282.142%2043.7269C288.053%2043.7269%20292.842%2049.0298%20292.842%2055.5859C292.842%2062.1218%20288.053%2067.4348%20282.142%2067.4348Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M515.69%2029.3618C515.69%2028.3092%20515.199%2027.0561%20512.884%2027.0561H510.32V34.4843H512.073V31.7777H512.684L514.167%2034.4743H516.141L514.417%2031.4669C515.239%2031.0659%20515.69%2030.3341%20515.69%2029.3618ZM512.754%2030.274H512.063V28.5397H512.754C513.756%2028.5397%20513.957%2028.8505%20513.957%2029.3517C513.957%2029.5823%20513.957%2030.274%20512.754%2030.274Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M512.854%2022.6654C508.366%2022.6654%20504.709%2026.3243%20504.709%2030.8153C504.709%2035.3163%20508.366%2038.9653%20512.854%2038.9653C517.343%2038.9653%20521%2035.3063%20521%2030.8153C521%2026.3243%20517.343%2022.6654%20512.854%2022.6654ZM512.854%2037.6019C509.117%2037.6019%20506.071%2034.5545%20506.071%2030.8153C506.071%2027.0762%20509.117%2024.0287%20512.854%2024.0287C516.592%2024.0287%20519.637%2027.0762%20519.637%2030.8153C519.637%2034.5645%20516.592%2037.6019%20512.854%2037.6019Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M130.41%2083.2736L106.725%2018.1343C102.777%207.29785%2092.4975%20-0.240579%2080.9754%200.0100337C71.0664%200.220548%2061.9589%205.50346%2056.8291%2014.0042L53.3424%2020.0089H53.3324L52.5408%2021.3823H33.7147C31.0897%2021.3823%2028.9556%2023.5175%2028.9556%2026.1439C28.9556%2028.7703%2031.0897%2030.9056%2033.7147%2030.9056H47.0203L47.0102%2030.9256H49.0241C51.8395%2030.9256%2054.1139%2033.2012%2054.1139%2036.0181C54.1139%2038.8349%2051.8395%2041.1105%2049.0241%2041.1105H41.1089L41.0989%2041.1205H4.75913C2.1341%2041.1105%200%2043.2357%200%2045.8621C0%2048.4885%202.1341%2050.6238%204.75913%2050.6238H35.6685L30.2581%2059.9867H30.1779L29.6469%2060.9089H15.8103C13.1853%2060.9089%2011.0512%2063.0441%2011.0512%2065.6705C11.0512%2068.297%2013.1853%2070.4322%2015.8103%2070.4322H24.2265L17.2531%2082.5017C15.3294%2085.8298%2017.7441%2090%2021.5814%2090H47.1605C50.7274%2090%2054.0237%2088.0954%2055.8171%2085.0078L64.4637%2070.0011H95.6135L101.244%2085.0078C102.336%2087.9149%20105.102%2089.8497%20108.208%2089.8697L125.681%2090C129.168%2090.01%20131.603%2086.5516%20130.41%2083.2736Z%22%20fill%3D%22%23FF7F32%22%2F%3E%20%3Cpath%20d%3D%22M71.2167%2050.8744C68.5215%2050.8744%2066.3273%2048.689%2066.3273%2045.9824C66.3273%2043.2858%2068.5115%2041.1005%2071.2167%2041.1005C73.9219%2041.1005%2076.1061%2043.2858%2076.1061%2045.9824C76.1061%2048.689%2073.9119%2050.8744%2071.2167%2050.8744Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3Cpath%20d%3D%22M89.5318%2050.8643C86.8266%2050.8643%2084.6425%2048.679%2084.6425%2045.9824C84.6425%2043.2758%2086.8266%2041.0804%2089.5318%2041.0804C92.227%2041.0804%2094.4212%2043.2758%2094.4212%2045.9824C94.4212%2048.669%2092.227%2050.8643%2089.5318%2050.8643Z%22%20fill%3D%22%230D4073%22%2F%3E%20%3C%2Fg%3E%20%3Cdefs%3E%20%3CclipPath%20id%3D%22clip0_46938_115605%22%3E%20%3Crect%20width%3D%22521%22%20height%3D%2290%22%20fill%3D%22white%22%2F%3E%20%3C%2FclipPath%3E%20%3C%2Fdefs%3E%20%3C%2Fsvg%3E")`;
}

// Converts a nested array of coordinates from [lng, lat] to [lat, lng] format
export function convertLngLatToLatLngCoordinates(
  coordinates: [number, number][][]
): [number, number][][] {
  try {
    return coordinates.map((polygon) =>
      polygon.map(([lng, lat]) => [lat, lng])
    );
  } catch (error) {
    return [];
  }
}

/**
 * Safely parses a JSON string into a specified generic type T.
 *
 * @template T The expected type of the parsed object.
 * @param {string | null | undefined} jsonString The JSON string to parse.
 * @returns {T | null} The parsed object of type T, or null if parsing fails or the input is invalid.
 */
export const safeParseJSON = <T>(
  jsonString: string | null | undefined
): T | null => {
  // 1. First, check if the input string is null, undefined, or empty.
  if (!jsonString) {
    return null;
  }

  try {
    // 2. Attempt to parse the string. This is the most reliable way
    //    to check if the string is valid JSON.
    const result: T = JSON.parse(jsonString);
    return result;
  } catch (error) {
    // 3. If JSON.parse() throws an error, the string is not valid JSON.
    //    Log the error for debugging and return null.
    console.error('Failed to parse JSON string:', error);
    return null;
  }
};

/**
 * The default list of fields to be removed from path objects.
 * Supports wildcard matching for fields ending with '*'.
 */
const DEFAULT_REMOVE_FIELDS: string[] = [
  'status',
  'cash_payment',
  'cash_collection',
  'pop_info',
  'proof_of_*', // Wildcard for any key starting with 'proof_of_'
  'arrival_lat',
  'arrival_lng',
  'location_sharing',
  'entrances',
  'geometry',
  'polygon',
];

/**
 * Creates a new object with specified fields removed.
 *
 * @template T - The type of the input object.
 * @param {T | null | undefined} pathObject - The input object.
 * @param {string[]} [fieldsToRemove=DEFAULT_REMOVE_FIELDS] - An optional array of field names to remove.
 * @returns {Partial<T>} A new object with the specified fields removed.
 */
export const cleanPathObject = <T extends Record<string, any>>(
  pathObject: T | null | undefined,
  fieldsToRemove: string[] = DEFAULT_REMOVE_FIELDS
): T => {
  // Return an empty object if the input is null, undefined, or not an object
  if (typeof pathObject !== 'object' || !pathObject) {
    return {} as T;
  }

  // Separate exact field names from wildcard prefixes
  const exactMatches = fieldsToRemove.filter((field) => !field.endsWith('*'));
  const prefixes = fieldsToRemove
    .filter((field) => field.endsWith('*'))
    .map((prefix) => prefix.slice(0, -1)); // Remove the '*'

  // Apply the filtering logic directly to the object's entries
  const filteredEntries = Object.entries(pathObject).filter(([key]) => {
    const isExactMatch = exactMatches.includes(key);
    const isPrefixMatch = prefixes.some((prefix) => key.startsWith(prefix));

    // Keep the entry only if it's NOT an exact match AND NOT a prefix match
    return !isExactMatch && !isPrefixMatch;
  });

  return Object.fromEntries(filteredEntries) as T;
};
